import { useState, useRef, useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import SocialAuthButtons from '@/components/SocialAuthButtons';
import { trackEvent } from '@/lib/analytics';
import { captureError } from '@/lib/errorReporting';
import { TYPO } from '@/constants/typography';
import { RADIUS, TRACKING } from '@/constants/designTokens';

import { getPasswordStrength, STRENGTH_CONFIG } from '@/lib/passwordStrength';

export default function SignUpScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { signUp } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const passwordRef = useRef<TextInput>(null);
  const confirmRef = useRef<TextInput>(null);

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  const invitesEnabled = process.env.EXPO_PUBLIC_INVITES_ENABLED === 'true';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const canSubmit = !loading && email.trim().length > 0 && password.length >= 8 && confirmPassword.length > 0 && (!invitesEnabled || inviteCode.trim().length > 0);

  const handleSignUp = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain at least one letter and one number.');
      return;
    }

    if (invitesEnabled) {
      if (!inviteCode.trim()) {
        setError('An invite code is required to sign up');
        return;
      }
      try {
        const { error: inviteError } = await supabase.rpc('consume_invite', {
          invite_code: inviteCode.trim(),
        });
        if (inviteError) {
          setError(inviteError.message || 'Invalid invite code');
          return;
        }
      } catch (err: any) {
        setError(err.message || 'Failed to validate invite');
        return;
      }
    }

    setLoading(true);
    setError('');

    const { data, error: signUpError } = await signUp(email, password);

    if (signUpError) {
      const msg = signUpError.message?.toLowerCase() ?? '';
      if (msg.includes('weak') || msg.includes('breach') || msg.includes('leaked') || msg.includes('easy to guess')) {
        setError('That password is too common. Please choose a stronger one (mix of letters, numbers, and symbols).');
        // Don't report to Sentry — this is expected user behavior
      } else if (msg.includes('already registered') || msg.includes('already been registered')) {
        setError('An account with this email already exists. Try signing in instead.');
        // Don't report to Sentry — expected
      } else {
        setError('Something went wrong creating your account. Please try again.');
        captureError(signUpError, { context: 'signup' }); // Only report unexpected errors
      }
      setLoading(false);
      return;
    }

    trackEvent('signup_completed');

    if (data?.session) {
      // If a returnTo param is present and is a safe route, navigate there after signup
      const returnTo = typeof params.returnTo === 'string' ? params.returnTo : null;
      const isSafeReturn =
        returnTo !== null &&
        (returnTo.startsWith('/org/invite/') || returnTo.startsWith('/org-roster'));
      if (isSafeReturn) {
        router.replace(returnTo as any);
      } else {
        router.replace('/gate');
      }
    } else {
      router.replace({ pathname: '/(auth)/check-email', params: { email } });
    }
    setLoading(false);
  };

  return (
    <>
      <KeyboardAvoidingView
        style={[styles.container, { backgroundColor: c.background }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={[styles.content, { paddingTop: insets.top + 16 }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]} accessibilityRole="header">Join DUEL</Text>
            <Text style={[styles.subtitle, { color: c.textSecondary }]}>
              Create your account to get started
            </Text>
          </View>

          {error ? (
            <View style={[styles.errorBox, { backgroundColor: c.surface, borderColor: c.error }]}>
              <Text style={[styles.errorText, { color: c.error }]}>{error}</Text>
            </View>
          ) : null}

          <View style={styles.form}>
            <View style={styles.inputWrap}>
              <Text style={[styles.label, { color: c.text }]}>Email</Text>
              <TextInput
                style={[styles.input, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
                value={email}
                onChangeText={(t) => { setEmail(t); setError(''); }}
                placeholder="your@email.com"
                placeholderTextColor={c.textTertiary}
                autoCapitalize="none"
                autoComplete="email"
                keyboardType="email-address"
                returnKeyType="next"
                onSubmitEditing={() => passwordRef.current?.focus()}
                editable={!loading}
                accessibilityLabel="Email address"
              />
            </View>

            <View style={styles.inputWrap}>
              <Text style={[styles.label, { color: c.text }]}>Password</Text>
              <TextInput
                ref={passwordRef}
                style={[styles.input, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
                value={password}
                onChangeText={(t) => { setPassword(t); setError(''); }}
                placeholder="Min 8 characters, letters & numbers"
                placeholderTextColor={c.textTertiary}
                secureTextEntry
                autoComplete="new-password"
                returnKeyType="next"
                onSubmitEditing={() => confirmRef.current?.focus()}
                editable={!loading}
                accessibilityLabel="Password"
              />
              {passwordStrength.level !== 'none' && (() => {
                const cfg = STRENGTH_CONFIG[passwordStrength.level as keyof typeof STRENGTH_CONFIG];
                return (
                  <View style={styles.strengthContainer}>
                    <View style={styles.strengthBarTrack}>
                      {[1, 2, 3, 4].map((i) => (
                        <View
                          key={i}
                          style={[
                            styles.strengthSegment,
                            { backgroundColor: passwordStrength.score >= i ? cfg.color : c.surfaceVariant },
                          ]}
                        />
                      ))}
                    </View>
                    <Text style={[styles.strengthLabel, { color: cfg.color }]}>
                      {cfg.label}
                    </Text>
                  </View>
                );
              })()}
            </View>

            <View style={styles.inputWrap}>
              <Text style={[styles.label, { color: c.text }]}>Confirm Password</Text>
              <TextInput
                ref={confirmRef}
                style={[styles.input, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
                value={confirmPassword}
                onChangeText={(t) => { setConfirmPassword(t); setError(''); }}
                placeholder="Re-enter password"
                placeholderTextColor={c.textTertiary}
                secureTextEntry
                autoComplete="new-password"
                returnKeyType="go"
                onSubmitEditing={handleSignUp}
                editable={!loading}
                accessibilityLabel="Confirm password"
              />
            </View>

            {invitesEnabled && (
              <View style={styles.inputWrap}>
                <Text style={[styles.label, { color: c.text }]}>Invite Code</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
                  value={inviteCode}
                  onChangeText={setInviteCode}
                  placeholder="Enter invite code"
                  placeholderTextColor={c.textTertiary}
                  editable={!loading}
                  accessibilityLabel="Invite code"
                />
              </View>
            )}

            <Pressable
              style={[styles.btn, { backgroundColor: c.accent }, !canSubmit && styles.btnDisabled]}
              onPress={handleSignUp}
              disabled={!canSubmit}
              accessibilityRole="button"
              accessibilityLabel="Create Account"
              android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
              hitSlop={{ top: 8, bottom: 8 }}>
              {loading ? (
                <ActivityIndicator color={c.accentForeground} />
              ) : (
                <Text style={[styles.btnText, { color: c.accentForeground }]}>Create Account</Text>
              )}
            </Pressable>

            <SocialAuthButtons />

            <TouchableOpacity onPress={() => {
              const gymParam = params.gym ? `?gym=${params.gym}` : '';
              router.push(`/(auth)/signin${gymParam}` as any);
            }} disabled={loading}>
              <Text style={[styles.linkText, { color: c.textSecondary }]}>
                Already have an account?{' '}
                <Text style={{ color: c.accent, fontWeight: '700' }}>Sign In</Text>
              </Text>
            </TouchableOpacity>

            <Text style={[styles.disclaimer, { color: c.textTertiary }]}>
              By signing up, you agree to our{' '}
              <Text
                style={[styles.legalLink, { color: c.textSecondary }]}
                onPress={() => router.push('/terms')}
              >
                Terms of Service
              </Text>
              {' '}and{' '}
              <Text
                style={[styles.legalLink, { color: c.textSecondary }]}
                onPress={() => router.push('/privacy')}
              >
                Privacy Policy
              </Text>
              .
            </Text>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { flex: 1, paddingHorizontal: 28, paddingBottom: 40 },
  header: { marginBottom: 40, gap: 12, alignItems: 'center' },
  logo: { width: 140, height: 140, marginBottom: 24 },
  title: { 
    fontSize: 32, 
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.normal,
  },
  subtitle: { 
    fontSize: 15, 
    fontFamily: TYPO.subheading.bold, 
    lineHeight: 21,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wider,
  },
  form: { gap: 24 },
  inputWrap: { gap: 8 },
  label: { 
    fontSize: 13, 
    fontFamily: TYPO.subheading.bold, 
    letterSpacing: TRACKING.wider,
    textTransform: 'uppercase',
  },
  input: { 
    borderWidth: 1, 
    borderRadius: RADIUS.xs, 
    paddingHorizontal: 16, 
    paddingVertical: 16, 
    fontSize: 16 
  },
  btn: { 
    paddingVertical: 16, 
    borderRadius: RADIUS.none, 
    alignItems: 'center', 
    marginTop: 8 
  },
  btnDisabled: { opacity: 0.5 },
  btnText: { 
    fontSize: 17, 
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
  linkText: { 
    textAlign: 'center', 
    fontSize: 14,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.normal,
  },
  disclaimer: { fontSize: 11, textAlign: 'center', marginTop: -4 },
  errorBox: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 12, marginBottom: 8 },
  errorText: { fontSize: 13, textAlign: 'center' },
  strengthContainer: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 6 },
  strengthBarTrack: { flexDirection: 'row', flex: 1, gap: 4, height: 4 },
  strengthSegment: { flex: 1, height: 4, borderRadius: 2 },
  strengthLabel: { fontSize: 10, fontWeight: '800', letterSpacing: 0.8, width: 80, textAlign: 'right' },
  legalLink: { textDecorationLine: 'underline', fontWeight: '600' },
});
